import { useMemo, useRef, useState, VoidFunctionComponent } from 'react';
import { useFrame } from '@react-three/fiber';
import { useMount } from 'react-use';
import { Group, Mesh, MeshPhysicalMaterial, PlaneGeometry, Vector3 } from 'three';
import { useTexture, Text, Environment } from '@react-three/drei';
import editionCardAlphaImg from '@/img/edition-card-alpha.png';
import cardColorImg from '@/img/edition-card-color.png';
import colorSwatchImg from '@/img/color-swatch.png';
import metadataMassImg from '@/img/metadata-mass.png';
import scrtchedMetalImg from '@/img/roughness-scratched-metal-1024.png';
import starImg from '@/img/star.png';
import { garamondNarrowLight, helveticaNeue } from '@/lib/fonts';
import { StatusField } from '@/components/scenes/edition/StatusField';
import { AttributeGroup } from '@/components/scenes/edition/AttributeGroup';
import type { Metadata, CardBounds } from '@/lib/types';
import { useResponsive } from '@/hooks/useResponsive';
import { legendaryCard, legendaryWhite, offblack } from '@/lib/colors';
import { useScratchedMetalRoughness } from '@/hooks/useScratchedMetalRoughness';
import { PortalMask } from '@/components/scenes/edition/PortalMask';
import { bnwEnvironmentFile } from '@/components/scenes/TitleScreen';

const MAX_MASS_OFFSET = 0.55; // found empirically

interface Props {
    botMetadata: Metadata;
    tokenId: number;
    width: number;
    height: number;
}

export const EditionCard: VoidFunctionComponent<Props> = ({ botMetadata, tokenId, width, height }) => {
    const frame = useTexture(editionCardAlphaImg);
    const cardColor = useTexture(cardColorImg);
    const colorSwatch = useTexture(colorSwatchImg);
    const scratchedMetal = useTexture(scrtchedMetalImg);
    const star = useTexture(starImg);
    const metadataMass = useTexture(metadataMassImg);
    const cardBorderMeshRef = useRef<Mesh>(new Mesh());
    const massGroupRef = useRef<Group>(new Group());
    const colorSwatchMaterialRef = useRef<MeshPhysicalMaterial>(new MeshPhysicalMaterial());
    const colorSwatchMeshRef = useRef<Mesh>(new Mesh());
    const colorSwatchGeometryRef = useRef<PlaneGeometry>(new PlaneGeometry());
    const cardBorderGeometryRef = useRef<PlaneGeometry>(new PlaneGeometry());
    const cardBorderMaterialRef = useRef<MeshPhysicalMaterial>(new MeshPhysicalMaterial());
    const textRef = useRef<Mesh>(new Mesh());
    const [cardBounds, setCardBounds] = useState<CardBounds>();
    const { responsiveValue } = useResponsive();
    const updatedMassPosition = useRef<boolean>(false);
    useScratchedMetalRoughness(
        colorSwatchMeshRef,
        colorSwatchMaterialRef,
        colorSwatchGeometryRef,
        cardBounds,
        !!cardBounds
    );

    const attributes: { label: string; value: string }[] = useMemo(() => {
        const traits = [];
        if (botMetadata.eyes) {
            traits.push({ label: 'Eyes', value: botMetadata.eyes });
        }
        if (botMetadata.arms) {
            traits.push({ label: 'Arms', value: botMetadata.arms });
        }
        if (botMetadata.legs) {
            traits.push({ label: 'Legs', value: botMetadata.legs });
        }
        if (botMetadata.top) {
            traits.push({ label: 'Top', value: botMetadata.top });
        }
        if (botMetadata.tail) {
            traits.push({ label: 'Tail', value: botMetadata.tail });
        }
        return traits;
    }, [botMetadata]);

    const legendary = botMetadata.class?.toLocaleLowerCase() === 'legendary';

    // calculate bounds of card in world coordinates so other components can
    // UV map from a common 2D texture space
    useMount(() => {
        if (cardBorderMaterialRef.current && cardBorderGeometryRef.current) {
            const worldPosition = new Vector3();
            cardBorderMeshRef.current.getWorldPosition(worldPosition);
            const worldScale = new Vector3();
            cardBorderMeshRef.current.getWorldScale(worldScale);
            cardBorderGeometryRef.current.computeBoundingBox();
            const boundingBox = cardBorderGeometryRef.current.boundingBox;

            // get bounds of card in world coordinates
            const xMax = worldPosition.x + worldScale.x * (boundingBox?.max.x || 0);
            const xMin = worldPosition.x + worldScale.x * (boundingBox?.min.x || 0);
            const yMax = worldPosition.y + worldScale.y * (boundingBox?.max.y || 0);
            const yMin = worldPosition.y + worldScale.y * (boundingBox?.min.y || 0);
            setCardBounds({
                x: [xMin, xMax],
                y: [yMin, yMax],
            });
            cardBorderMaterialRef.current.roughnessMap = scratchedMetal;
        }
    });

    const darkEye = useMemo(() => {
        if (botMetadata.eyeColor) {
            const { r, g, b } = botMetadata.eyeColor;
            return r + g + b < 0.3;
        }
        return false;
    }, [botMetadata.eyeColor]);

    const cardBorderProps = legendary
        ? {
              color: legendaryCard,
              metalness: 1,
          }
        : {
              color: 'black',
              clearcoat: 0.1,
          };

    useFrame(() => {
        if (textRef.current && massGroupRef.current && !updatedMassPosition.current) {
            const { boundingBox } = textRef.current.geometry;
            if (boundingBox) {
                const { x: xMax } = boundingBox.max;
                const { x: xMin } = boundingBox.min;
                if (isFinite(xMax) && isFinite(xMin)) {
                    const width = boundingBox ? boundingBox.max.x - Math.abs(boundingBox.min.x) : 0;
                    if (width) {
                        const shiftPosition = width < MAX_MASS_OFFSET ? MAX_MASS_OFFSET - width : 0;
                        massGroupRef.current.position.x += shiftPosition;
                        updatedMassPosition.current = true;
                    }
                }
            }
        }
    });

    return (
        <group scale={responsiveValue(1.65, 1.6)} position={[0, responsiveValue(0, -0.2), 0]}>
            <Environment background={false} files={bnwEnvironmentFile} />
            <PortalMask width={width} height={height} />
            <group scale={1.01}>
                <mesh>
                    <planeGeometry attach="geometry" args={[width, height]} />
                    <meshStandardMaterial color={botMetadata.eyeColor} map={cardColor} transparent depthWrite={false} />
                </mesh>
                <mesh ref={cardBorderMeshRef}>
                    <planeGeometry ref={cardBorderGeometryRef} attach="geometry" args={[width, height]} />
                    <meshPhysicalMaterial
                        ref={cardBorderMaterialRef}
                        alphaMap={frame}
                        transparent
                        depthWrite={false}
                        {...cardBorderProps}
                    />
                </mesh>
            </group>
            <Text
                letterSpacing={-0.05}
                position={[responsiveValue(-0.93, -0.891), responsiveValue(1.56, 1.55), 0.01]}
                font={helveticaNeue}
                fontSize={responsiveValue(0.068, 0.065)}
                color={legendary ? legendaryWhite : offblack}
                maxWidth={2}
            >
                {botMetadata.batch.toString().padStart(2, '0')}
            </Text>

            <group position={[0, -1.46, 0.01]}>
                <group position={[-0.98, 0, 0]}>
                    <mesh>
                        <planeGeometry attach="geometry" args={[0.14, 0.14]} />
                        <meshPhysicalMaterial
                            ref={colorSwatchMaterialRef}
                            color={botMetadata.bodyColor}
                            map={colorSwatch}
                            transparent
                            {...botMetadata.preset}
                        />
                    </mesh>
                    <Text
                        letterSpacing={-0.05}
                        anchorX="left"
                        position={[0.108, -0.0385, 0]}
                        font={helveticaNeue}
                        fontSize={0.065}
                        color={legendary ? legendaryWhite : offblack}
                        maxWidth={2}
                    >
                        {botMetadata.bodyMaterialName}
                    </Text>
                </group>
                <group ref={massGroupRef} position={[0.4, 0.01, 0]}>
                    <mesh>
                        <planeGeometry attach="geometry" args={[0.18, 0.18]} />
                        <meshPhysicalMaterial
                            emissive={legendary ? legendaryWhite : 'black'}
                            emissiveIntensity={legendary ? 1 : 0}
                            color={legendary ? legendaryWhite : 'black'}
                            map={metadataMass}
                            transparent
                        />
                    </mesh>
                    <Text
                        ref={textRef}
                        anchorX="left"
                        letterSpacing={-0.05}
                        position={[0.12, -0.045, 0]}
                        font={helveticaNeue}
                        fontSize={0.07}
                        color={legendary ? legendaryWhite : offblack}
                        maxWidth={2}
                    >
                        {`${Math.round(botMetadata.mass)} metagrams`}
                    </Text>
                </group>
            </group>
            <Text
                position={[0, 1.292, 0.01]}
                textAlign="center"
                font={garamondNarrowLight}
                fontSize={0.175}
                letterSpacing={-0.05}
                color={legendary ? offblack : 'white'}
                maxWidth={2}
                renderOrder={1}
            >
                {botMetadata.name.toUpperCase()}
            </Text>

            {botMetadata.status && cardBounds && (
                <group position={[0, 1.045, 0.01]}>
                    <StatusField
                        cardBounds={cardBounds}
                        legendary={legendary}
                        label="Status"
                        value={botMetadata.status}
                        width={width}
                    />
                </group>
            )}

            {cardBounds && (
                <group position={[0, -0.91, 0.01]}>
                    <AttributeGroup
                        legendary={legendary}
                        width={width}
                        attributes={attributes}
                        cardBounds={cardBounds}
                    />
                </group>
            )}

            <group position={[0.97, 1.305, 0.01]}>
                {legendary && (
                    <>
                        <mesh position={[-0.015, 0.115, 0]} renderOrder={1}>
                            <planeGeometry attach="geometry" args={[0.06, 0.06]} />
                            <meshPhysicalMaterial clearcoat={1} map={star} transparent />
                        </mesh>
                        <mesh position={[0.055, 0.14, 0]} renderOrder={1}>
                            <planeGeometry attach="geometry" args={[0.08, 0.08]} />
                            <meshPhysicalMaterial clearcoat={1} map={star} transparent />
                        </mesh>
                        <mesh position={[0.12, 0.095, 0]} renderOrder={1}>
                            <planeGeometry attach="geometry" args={[0.05, 0.05]} />
                            <meshPhysicalMaterial clearcoat={1} map={star} transparent />
                        </mesh>
                    </>
                )}
                <Text
                    letterSpacing={-0.02}
                    font={helveticaNeue}
                    fontSize={0.105}
                    color={legendary || darkEye ? legendaryWhite : offblack}
                    maxWidth={2}
                    renderOrder={1}
                >
                    {`#${tokenId}`}
                </Text>
            </group>
        </group>
    );
};
